import React from 'react'
import styled from '@emotion/styled'
import { countries } from '../../Constants/countries.js'
import { unitedStates } from '../../Constants/states.js'
import { MasterCard, Visa } from '../../Assets/svg/'
import AmexLogo from '../../Assets/svg/amex.png'
import { Button, Grid, Autocomplete, TextField } from '@mui/material'
import { removeAcentos } from '../../Utils/stringUtils.js'
import { getMonth, getYear } from 'date-fns'
import creditCardType, { types as CardType } from 'credit-card-type'

const BillingInfoContainer = styled('div')(({ theme, thirdParty }) => ({
  fontSize: '.75rem',
  width: '100%',
  color: theme.palette.primary.main,
  padding: '0 2rem',
  display: 'flex',
  flexDirection: thirdParty ? 'row' : 'column',
  flexWrap: thirdParty ? 'wrap' : null,
  justifyContent: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    marginTop: '2rem',
    flexDirection: thirdParty ? 'column' : 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
  },
}))

const InputStyle = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginTop: '15px',
  color: theme.palette.text.accent,
  '& div': {
    height: '40px',
  },
  [theme.breakpoints.between('xs', 'md')]: {
    width: '48%',
  },
  [theme.breakpoints.down('xs')]: {
    width: '100%',
  },
}))

// const AutocompleteStyle = styled(Autocomplete)(({ theme }) => ({
//   width: '100%',
//   backgroundColor: 'white',
//   [theme.breakpoints.down('sm')]: {
//     width: '48%',
//   },
//   [theme.breakpoints.only('xs')]: {
//     width: '100%',
//   },
// }))

const CityZipWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  '& :first-of-type': {
    marginRight: '1.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    width: '48%',
  },
  [theme.breakpoints.only('xs')]: {
    width: '100%',
  },
}))

const CardWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  marginTop: '15px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '0.5rem',
  },
}))

const VisaStyle = styled(Visa)({
  height: '5em',
})

const MasterCardStyle = styled(MasterCard)({
  height: '5em',
})

const BillingAddressContainer = styled(Grid)(({ theme, thirdParty }) => ({
  width: thirdParty ? '50%' : '100%',
  padding: thirdParty ? '1rem' : '0',
  color: theme.palette.primary.main,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    marginTop: '0.5rem',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    width: '100%',
  },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root': {
    padding: '0px 9px',
  },
  '& .css-1rndd9j-MuiPopper-root-MuiAutocomplete-popper .css-1s5lphu-MuiPopper-root-MuiAutocomplete-popper .css-ojwayj': {
    backgroundColor: 'white',
  },
  '& .MuiAutocomplete-popper': {
    backgroundColor: 'white',
  },
}))

const HeaderContainer = styled(Grid)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: '1rem',
  [theme.breakpoints.down('xs')]: {
    flexDirection: 'column',
  },
}))

function BillingInfo(props) {
  const { values, handleChange, errors, setFieldValue, setFieldError } =
    props.formikProps
  const { thirdParty = false, setCreditCard } = props

  //Autocomplete Country
  const [autoCompleteText, setAutoCompleteText] = React.useState('')
  const [autoCompleteValue, setAutoCompleteValue] = React.useState(
    values.billingCountry
  )
  //Autocomplete State
  const [autoCompleteTextState, setAutoCompleteTextState] = React.useState('')
  const [autoCompleteValueState, setAutoCompleteValueState] = React.useState(
    values.billingState
  )
  const [cardTypes, setCardTypes] = React.useState([])
  const [cardInfo, setCardInfo] = React.useState([])

  const hanldeExpirationDate = (demo) => {
    if (demo.target.value.length === 1) {
      if (Number(demo.target.value)) {
        if (Number(demo.target.value) > 1) {
          setFieldValue('expirationDate', `0${demo.target.value}YY`)
        } else {
          setFieldValue('expirationDate', `${demo.target.value}MYY`)
        }
      }
    } else {
      const poping = 5 - demo.target.value.split('').length
      const expirationDate = demo.target.value
        .replace(/M/g, '')
        .replace(/Y/g, '')
        .replace(/\//g, '')
        .split('')
      for (let i = 0; i < poping; i++) {
        expirationDate.pop()
      }
      while (expirationDate.length > 4) {
        expirationDate.pop()
      }
      setFieldValue('expirationDate', expirationDate.join(''))
    }
  }

  const formatExpirationDate = (date) => {
    const firstDigit = Number(date.charAt(0)) ? Number(date.charAt(0)) : 0
    const formattedDate = firstDigit > 1 ? '0' + date : date
    const filledDate = formattedDate.padEnd(2, 'M').padEnd(4, 'Y')
    return filledDate
      .split('')
      .map((char, charIndex) => {
        return charIndex !== 2 ? char : `/${char}`
      })
      .join('')
    // 1234 -> 12/12
    // 1    -> 1-/--
  }

  const removeAccentsOnBlur = (e) => {
    const { id, value } = e.target
    setFieldValue(id, removeAcentos(value || ''))
  }

  const validateExpirationDate = (e) => {
    const currentYear = getYear(new Date()).toString().substring(2)
    const currentMonth = getMonth(new Date()) + 1

    const expirationYear = e.target.value.substring(3)
    const expirationMonth = e.target.value.substring(0, 2)

    const completeInfo = () => {
      if (
        expirationYear.match(/^[0-9]{2}$/) &&
        expirationMonth.match(/^[0-9]{2}$/)
      ) {
        return true
      } else {
        return false
      }
    }

    if (completeInfo()) {
      if (currentYear > expirationYear) {
        setFieldValue('expirationDate', `${expirationMonth}${expirationYear}`)
        setFieldError('expirationDate', 'Invalid year')
      } else {
        if (expirationMonth > currentMonth) {
          setFieldValue('expirationDate', `${expirationMonth}${expirationYear}`)
        } else {
          setFieldError('expirationDate', 'Invalid month')
          setFieldValue('expirationDate', `${expirationMonth}${expirationYear}`)
        }
      }
    }
  }

  const formatCreditCardNumber = (card) => {
    if (cardInfo?.length) {
      const gaps = cardInfo[0].gaps?.reverse()
      const splittedCardNumber = card
        .replaceAll(' ', '')
        .split('')
        .filter((el) => !Number.isNaN(Number(el)))

      gaps
        .filter((el) => el < splittedCardNumber.length)
        .forEach((el) => {
          splittedCardNumber.splice(el, 0, ' ')
        })
      const formattedCardNumber = splittedCardNumber.join('')
      console.log('FORMATTED CARD NUMBER: ', formattedCardNumber)
      return formattedCardNumber
    }

    return card
  }

  const handleCardNumber = (e) => {
    if (e.target.value.length < 2) {
      setFieldValue('cardNumber', `${e.target.value}`)
    } else {
      const cardNum = e.target.value
      const cardTypes = creditCardType(cardNum)

      if (cardTypes.length) {
        const theCreditCardTypes = cardTypes.map((card) => {
          return card.type
        })
        setCardTypes(theCreditCardTypes)
        setCardInfo(cardTypes)
        setCreditCard && setCreditCard(cardTypes[0])
        setFieldValue('cardNumber', formatCreditCardNumber(cardNum))
      } else {
        setFieldError(
          'cardNumber',
          'Card type not recognize, please try with a different number'
        )
      }
    }
  }

  return (
    <BillingInfoContainer thirdParty={thirdParty}>
      <h2 style={{ marginBottom: '1rem' }}>CREDIT CARD INFORMATION</h2>
      <HeaderContainer>
        <Button
          variant="contained"
          color="secondary"
          style={{ marginTop: '5px', fontSize: '10px', marginRight: '2px' }}
          size="medium"
          onClick={() => {
            setFieldValue('cardholderName', `${values.contactName}`)
            setFieldValue('cardholderLastName', `${values.contactLastName}`)
          }}
        >
          Copy name from contact
        </Button>
        {/* {isBOE && (
          <Button
            variant="contained"
            color="secondary"
            style={{ marginTop: '5px', fontSize: '10px' }}
            size="medium"
            onClick={() => {
              setFieldValue('billingAddress1', values.operatorAddress1)
              setFieldValue('billingAddress2', values.operatorAddress2)
              setFieldValue('billingCountry', values.operatorCountry)
              setFieldValue('billingState', values.operatorState)
              setFieldValue('billingCity', values.operatorCity)
              setFieldValue('billingZipCode', values.operatorZipCode)
              setAutoCompleteText(values.operatorCountry || '')
              setAutoCompleteValue(values.operatorCountry || '')
              setAutoCompleteValueState(values.operatorState || '')
              setAutoCompleteTextState(values.operatorState || '')
            }}
          >
            Copy address from Operator
          </Button>
        )} */}
      </HeaderContainer>
      <BillingAddressContainer thirdParty={thirdParty}>
        <InputStyle
          id="cardholderName"
          error={!!errors.cardholderName}
          helperText={errors?.cardholderName && errors?.cardholderName}
          name="cardholderName"
          value={values.cardholderName}
          label="CARDHOLDER NAME *"
          focused
          onChange={handleChange}
          onBlur={removeAccentsOnBlur}
        />
        <InputStyle
          id="cardholderLastName"
          error={!!errors.cardholderLastName}
          helperText={errors?.cardholderLastName && errors?.cardholderLastName}
          name="cardholderLastName"
          value={values.cardholderLastName}
          label="CARDHOLDER LAST NAME"
          focused
          onChange={handleChange}
          onBlur={removeAccentsOnBlur}
        />
        <InputStyle
          id="cardNumber"
          error={!!errors.cardNumber}
          helperText={errors?.cardNumber && errors?.cardNumber}
          name="cardNumber"
          label="CARD NUMBER *"
          focused
          onChange={handleCardNumber}
          value={values.cardNumber}
          type="text"
        />
        <CardWrapper>
          {cardTypes.includes(CardType.AMERICAN_EXPRESS) && (
            <img
              alt="American express"
              src={AmexLogo}
              style={{ height: '5em' }}
            />
          )}
          {cardTypes.includes(CardType.MASTERCARD) && <MasterCardStyle />}
          {cardTypes.includes(CardType.VISA) && <VisaStyle />}
        </CardWrapper>
        <CityZipWrapper>
          <InputStyle
            id="expirationDate"
            error={!!errors.expirationDate}
            helperText={errors?.expirationDate && errors?.expirationDate}
            name="expirationDate"
            value={formatExpirationDate(values.expirationDate)}
            label="EXPIRATION DATE *"
            focused
            onChange={hanldeExpirationDate}
            onBlur={validateExpirationDate}
          />
          <InputStyle
            id="cvv"
            error={!!errors.cvv}
            helperText={errors?.cvv && errors?.cvv}
            name="cvv"
            label="CVV *"
            focused
            onChange={handleChange}
            value={values.cvv}
            type="text"
          />
        </CityZipWrapper>
      </BillingAddressContainer>
      <BillingAddressContainer thirdParty={thirdParty}>
        <InputStyle
          id="billingAddress1"
          error={!!errors.billingAddress1}
          helperText={errors?.billingAddress1 && errors?.billingAddress1}
          name="billingAddress1"
          label="ADDRESS *"
          focused
          onChange={handleChange}
          value={values.billingAddress1}
          onBlur={removeAccentsOnBlur}
        />
        <InputStyle
          id="billingAddress2"
          error={!!errors.billingAddress2}
          helperText={errors?.billingAddress2 && errors?.billingAddress2}
          name="billingAddress2"
          label="ADDRESS 2"
          focused
          onChange={handleChange}
          value={values.billingAddress2}
          onBlur={removeAccentsOnBlur}
        />
        <InputStyle
          id="billingCity"
          error={!!errors.billingCity}
          helperText={errors?.billingCity && errors?.billingCity}
          name="billingCity"
          label="CITY *"
          focused
          onChange={handleChange}
          value={values.billingCity}
          onBlur={removeAccentsOnBlur}
        />
        <Autocomplete
          id="billingCountry"
          // error={!!errors.billingCountry}
          //helperText={errors?.billingCountry && errors?.billingCountry}
          onChange={(e, newInputValue) => {
            setFieldValue('billingCountry', newInputValue?.itemLabel)
            setAutoCompleteValue(newInputValue?.itemLabel || '')
            setAutoCompleteText(newInputValue?.itemLabel || '')
          }}
          disablePortal
          onBlur={removeAccentsOnBlur}
          value={autoCompleteValue}
          inputValue={autoCompleteText}
          onInputChange={(event, newInputValue) => {
            setAutoCompleteText(newInputValue || '')
          }}
          name="billingCountry"
          options={countries || []}
          getOptionLabel={(option) => option?.itemLabel || autoCompleteValue}
          renderInput={(params) => (
            <TextField
              {...params}
              label="COUNTRY *"
              margin="normal"
              variant="outlined"
              fullWidth
            />
          )}
          fullWidth
        />
        {values.billingCountry === 'United States of America' ? (
          <Autocomplete
            id="billingState"
            // error={!!errors.billingCountry}
            //helperText={errors?.billingCountry && errors?.billingCountry}
            onChange={(e, newInputValue) => {
              setFieldValue('billingState', newInputValue?.itemLabel)
              setAutoCompleteValueState(newInputValue?.itemLabel || '')
              setAutoCompleteTextState(newInputValue?.itemLabel || '')
            }}
            value={autoCompleteValueState}
            inputValue={autoCompleteTextState}
            onInputChange={(event, newInputValue) => {
              setAutoCompleteTextState(newInputValue || '')
            }}
            disablePortal
            fullWidth
            name="billingState"
            options={unitedStates || []}
            getOptionLabel={(option) =>
              option?.itemLabel || autoCompleteValueState
            }
            renderInput={(params) => (
              <TextField {...params} label="STATE *" margin="normal" />
            )}
          />
        ) : (
          <InputStyle
            id="billingState"
            error={!!errors.billingState}
            helperText={errors?.billingState && errors?.billingState}
            name="billingState"
            label="STATE *"
            focused
            onChange={handleChange}
            value={values.billingState}
            onBlur={removeAccentsOnBlur}
          />
        )}
        <InputStyle
          id="billingZipCode"
          error={!!errors.billingZipCode}
          helperText={errors?.billingZipCode && errors?.billingZipCode}
          name="billingZipCode"
          label="ZIP CODE *"
          focused
          onChange={handleChange}
          value={values.billingZipCode}
        />
      </BillingAddressContainer>
    </BillingInfoContainer>
  )
}

export default BillingInfo
